import { FileWithPreview } from 'react-dropzone';
import { Crop, PixelCrop } from 'react-image-crop';

import { BannerId, ImageEditorId } from '../../typings/banners';
import { Size } from '../../typings/common';
import { BannerImageSize } from '../../typings/domain';
import { GdAdImageNamespace } from '../../typings/graphql-grid';
import { ExtendedBlob } from '../ExtendedBlob/ExtendedBlob';

export const calcCropSelection = (size: Size, preferRatio: number[]): PixelCrop => {
    if (!preferRatio.length) {
        return { x: 0, y: 0, height: size.height, width: size.width };
    }

    const imageRatio = size.width / size.height;
    const selectionRatio = preferRatio.sort((a, b) => Math.abs(imageRatio - a) - Math.abs(imageRatio - b))[0];
    let x = 0,
        y = 0,
        newWidth = size.width,
        newHeight = size.height;

    if (selectionRatio && !isImageRespondAspectCropQuality(newWidth, newHeight, selectionRatio)) {
        if (imageRatio > selectionRatio) {
            x = Math.round((size.width - size.height * selectionRatio) / 2);
            newWidth = Math.round(newHeight * selectionRatio);
        } else {
            y = Math.round((size.height - size.width / selectionRatio) / 2);
            newHeight = Math.round(newWidth / selectionRatio);
        }
    }

    return {
        x: x,
        y: y,
        height: newHeight,
        width: newWidth
    };
};

export const isImageRespondAspectCropQuality = (width: number, height: number, selectionRatio: number): boolean => {
    return Math.abs(width / selectionRatio - height) <= 0.5;
};

export const convertCropToPercentCrop = (size: Size, pixelCrop: PixelCrop): Crop => {
    return {
        x: (pixelCrop.x * 100) / size.width,
        y: (pixelCrop.y * 100) / size.height,
        height: (pixelCrop.height * 100) / size.height,
        width: (pixelCrop.width * 100) / size.width
    };
};

export const getUploadingImagesHash = (images: FileWithPreview[]): string => {
    return images.reduce((result, image) => {
        return result + `#${image.name}/${image.size}/${image.lastModified}`;
    }, '');
};

export const isImageHasSize = (size: BannerImageSize, image: BannerImageSize): boolean => {
    return (
        (size.isAdaptive && size.isAdaptive === image.isAdaptive) ||
        (size.height === image.height && size.width === image.width)
    );
};

export const isWideImage = (width: number, height: number): boolean | undefined => {
    if (width && height) {
        return Math.abs((width * 9) / 16 - height) <= 0.5;
    }

    return undefined;
};

export const getUrlFromImageItem = (
    mdsGroupId: string | null,
    namespace: GdAdImageNamespace,
    imageHash: string
): string => {
    let url;

    if (mdsGroupId) {
        const imageNamespace = namespace === 'DIRECT_PICTURE' ? 'direct-picture' : 'direct';

        url = `/images/${imageNamespace}/${mdsGroupId}/${imageHash}`;
    } else {
        url = `/images/${imageHash}`;
    }
    return url;
};

export const convertGifToImage = (file: FileWithPreview): Promise<ExtendedBlob> => {
    return new Promise((resolve, reject): void => {
        const reader = new FileReader();

        reader.onload = function(e: ProgressEvent & { target: { result: string } }): void {
            const image = new Image();

            image.src = e.target.result;

            image.onload = function(): void {
                const size = this as HTMLImageElement;
                const width = size.width;

                const height = size.height;

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = width;
                canvas.height = height;

                if (!ctx) {
                    reject('Canvas getContext("2d") is not exist');
                    return;
                }

                ctx.drawImage(image, 0, 0, width, height, 0, 0, width, height);

                canvas.toBlob(
                    blob => {
                        const firstImageName = [file.name.slice(0, file.name.indexOf('.gif')), 'jpg'].join('.');

                        if (!blob) {
                            reject('blob is null');
                            return;
                        }

                        const firstImage = new ExtendedBlob(blob, firstImageName, file.lastModified);
                        resolve(firstImage);
                    },
                    'image/jpeg',
                    0.75
                );
            };
        };
        reader.onerror = function(error: ProgressEvent): void {
            reject(error);
        };

        reader.readAsDataURL(file);
    });
};

const imageEditorId2BannerIdMap = new Map<ImageEditorId, BannerId>();

export const getImageEditorIdByBannerId = (bannerId: BannerId, ...tail: string[]): ImageEditorId => {
    const imageEditorId: ImageEditorId = [bannerId, ...tail].join('#');

    imageEditorId2BannerIdMap.set(imageEditorId, bannerId);

    return imageEditorId;
};

export const getBannerIdByImageEditorId = (imageEditorId: ImageEditorId): BannerId => {
    return imageEditorId2BannerIdMap.get(imageEditorId) || imageEditorId;
};
